import { Box, styled, Typography } from '@mui/material';
import { PropsWithChildren } from 'react';
import DESIGN_COLORS from 'shared/_ui/design-colors';

/**
 * File row on a single line. Fields appear as space allows:
 * <sm (600) title | sm+ title, id | md (900)+ title, date, id | lg (1200)+ title, date, size, id.
 * Leading thumbnail / type icon and the action icons are always shown.
 */
export const ProjectFileContainer = styled(Box, {
	name: 'ProjectFileContainer',
	shouldForwardProp: (propName: string) => !['hasDescription'].includes(propName),
})<{ hasDescription: boolean }>(({ hasDescription, theme }) => {
	const areas = (columns: string) =>
		`"${columns}"${hasDescription ? ` "${columns.replace('title', 'description')}"` : ''}`;
	return {
		'&:hover': { backgroundColor: DESIGN_COLORS.surfaceMuted },
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
		justifyContent: 'space-between',
		display: 'grid',
		padding: theme.spacing(2.5, 2),
		borderBottom: `1px solid ${DESIGN_COLORS.borderSoft}`,
		'&:last-child': { borderBottom: 'none' },

		alignItems: 'center',
		columnGap: theme.spacing(3),
		gridTemplateAreas: areas('image title icons'),
		gridTemplateColumns: 'auto minmax(0, 1fr) auto',

		[theme.breakpoints.up('sm')]: {
			gridTemplateAreas: areas('image title id icons'),
			gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
		},
		[theme.breakpoints.up('md')]: {
			gridTemplateAreas: areas('image title date id icons'),
			gridTemplateColumns: 'auto minmax(0, 1fr) auto auto auto',
		},
		[theme.breakpoints.up('lg')]: {
			gridTemplateAreas: areas('image title date size id icons'),
			gridTemplateColumns: 'auto minmax(0, 1fr) auto auto auto auto',
		},
	};
});

/** Thumbnail slot of a finished file: video 96x54 (70x40 below `sm`), audio 50x50 (40x40 below `sm`). */
export const ProjectFileImageContainer = styled(Box, {
	name: 'ProjectFileImageContainer',
	shouldForwardProp: (propName: string) => !['square'].includes(propName),
})<{ square?: boolean }>(({ square, theme }) => ({
	gridArea: 'image',
	flex: 'none',
	overflow: 'hidden',
	width: square ? '2.5rem' : '4.375rem',
	height: '2.5rem',
	borderRadius: square ? '0.4375rem' : '0.5rem',
	// Placeholder shown while loading / without an image: video gradient, flat dark for audio.
	background: square
		? DESIGN_COLORS.dark2
		: `linear-gradient(135deg, ${DESIGN_COLORS.dark2}, ${DESIGN_COLORS.slate700})`,
	[theme.breakpoints.up('sm')]: {
		width: square ? '3.125rem' : '6rem',
		height: square ? '3.125rem' : '3.375rem',
	},
	// CloudImage sizes itself by width only; fill the slot (cropping like `object-fit: cover`).
	'& img, & .MuiSkeleton-root': { width: '100%', height: '100%' },
	'& > div': { backgroundColor: 'transparent' },
	'& .MuiSkeleton-root': { backgroundColor: 'transparent' },
}));

/** 2rem rounded square with the file-type icon, shown before non-finished file rows. */
export const ProjectFileTypeIcon = styled(Box, { name: 'ProjectFileTypeIcon' })({
	gridArea: 'image',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '2rem',
	height: '2rem',
	borderRadius: '0.5rem',
	backgroundColor: DESIGN_COLORS.borderSoft,
	color: DESIGN_COLORS.muted,
	'& .MuiSvgIcon-root': { fontSize: '1rem' },
});

export const ProjectFileTitle = styled('div', {
	name: 'ProjectFileTitle',
})({
	gridArea: 'title',
	minWidth: 0,
});

/** Wrapper for date + size; its children are placed in the row grid directly. */
export const ProjectFileDateSizeContainer = styled('div', {
	name: 'ProjectFileDateSizeContainer',
})({ display: 'contents' });

/** Created date — visible from `md`. */
export const ProjectFileDate = ({ children }: PropsWithChildren) => {
	return (
		<Typography
			color="textSecondary"
			noWrap
			variant="body1"
			component="span"
			sx={(theme) => ({
				gridArea: 'date',
				display: 'none',
				[theme.breakpoints.up('md')]: {
					display: 'block',
					minWidth: '6rem',
					textAlign: 'left',
				},
			})}
		>
			{children}
		</Typography>
	);
};

/** File size — visible from `lg`. */
export const ProjectFileSize = ({ children }: PropsWithChildren) => {
	return (
		<Typography
			color="textSecondary"
			noWrap
			variant="body1"
			component="span"
			sx={(theme) => ({
				gridArea: 'size',
				display: 'none',
				[theme.breakpoints.up('lg')]: {
					display: 'block',
					minWidth: '8.265ex', // ~60px for font-size: 14px
					textAlign: 'right',
				},
			})}
		>
			{children}
		</Typography>
	);
};

export const ProjectFileDescription = styled(Typography, {
	name: 'ProjectFileDescription',
})(({ theme }) => ({
	gridArea: 'description',
	fontSize: '0.75rem', // 12px
	color: DESIGN_COLORS.muted,
	display: 'none',
	[theme.breakpoints.up('sm')]: { display: 'block' },
}));
ProjectFileDescription.defaultProps = {
	...ProjectFileDescription.defaultProps,
	noWrap: true,
};

export const ProjectFileIconsContainer = styled(Box, {
	name: 'ProjectFileIconsContainer',
})({
	gridArea: 'icons',
	display: 'flex',
	flexDirection: 'row-reverse',
});

/** YouTube + share icons of finished files; hidden below `sm` (600px). */
export const ProjectFileFinishIconsContainer = styled(Box, {
	name: 'ProjectFileFinishIconsContainer',
	shouldForwardProp: (propName: string) => !['isFinish'].includes(propName),
})<{ isFinish: boolean }>(({ isFinish, theme }) => ({
	display: 'none',
	gap: theme.spacing(2),
	alignItems: 'center',
	marginLeft: 'auto',
	...(isFinish && { [theme.breakpoints.up('sm')]: { display: 'flex' } }),
}));
